import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { LiaBrainSolid, LiaInfoCircleSolid, LiaPaperPlane, LiaTimesSolid } from 'react-icons/lia';

import { Button } from '@/components-new/button';
import { Drawer, DrawerBody, DrawerHeader, DrawerTitle } from '@/components-new/drawer';
import { useAskMutation } from '@/features/ai/api';
import { useDelayedClose } from '@/hooks/use-delayed-close';
import { markdownSerializer } from '@/shared/markdown/markdown-serialization';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

type AskArtiaPanelProps = {
  open: boolean;
  onClose: () => void;
};

export const AskArtiaPanel = ({ open, onClose }: AskArtiaPanelProps) => {
  const { isOpen, handleOpen, handleClose } = useDelayedClose(open, onClose);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate, isPending } = useAskMutation();

  useEffect(() => {
    if (open) {
      handleOpen();
    }
  }, [open, handleOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isPending) {
      inputRef.current?.focus();
    }
  }, [isOpen, isPending]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || isPending) return;

    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');

    mutate(
      { question, conversationId },
      {
        onSuccess: (data) => {
          setConversationId(data.conversationId);
          setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
        },
        onError: () => {
          setMessages(prev => [
            ...prev,
            { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' },
          ]);
        },
      }
    );
  };

  const handleClearChat = () => {
    setMessages([]);
    setConversationId(undefined);
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} size="3xl">
      <DrawerHeader>
        <DrawerTitle>
          <div className="flex items-center gap-2">
            <LiaBrainSolid className="size-6 text-secondary-600" />
            Ask Artia
          </div>
        </DrawerTitle>
        <div className="flex items-center gap-2">
          {messages.length > 0 && (
            <Button plain onClick={handleClearChat}>
              Clear
            </Button>
          )}
          <Button plain onClick={handleClose}>
            <LiaTimesSolid />
          </Button>
        </div>
      </DrawerHeader>
      <DrawerBody className="flex-1">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <LiaBrainSolid className="size-12 text-gray-300" />
              <p className="text-sm font-medium text-gray-900">Ask Artia anything</p>
              <p className="max-w-sm text-sm text-gray-500">
                Ask about drug coverage, state policies, upcoming meetings, or any Medicaid-related question.
              </p>
              <p className="mt-2 text-xs text-gray-400">
                Powered by Artia Intelligence
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={clsx(
                'flex',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={clsx(
                  'max-w-[85%] rounded-lg px-4 py-3',
                  message.role === 'user'
                    ? 'bg-primary-950 text-white'
                    : 'bg-gray-100 text-gray-900'
                )}
              >
                {message.role === 'assistant' ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: markdownSerializer.deserialize(message.content),
                    }}
                  />
                ) : (
                  <p className="text-sm">{message.content}</p>
                )}
              </div>
            </div>
          ))}

          {isPending && (
            <div className="flex justify-start">
              <div className="rounded-lg bg-gray-100 px-4 py-3">
                <div className="flex items-center gap-1">
                  <span className="size-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                  <span className="size-2 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                  <span className="size-2 animate-bounce rounded-full bg-gray-400" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </DrawerBody>

      {/* Input area — sticky to bottom like DrawerHeader is sticky to top */}
      <form onSubmit={handleSubmit} className="sticky bottom-0 z-50 border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            disabled={isPending}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500 disabled:opacity-50"
          />
          <Button
            type="submit"
            color="secondary"
            disabled={!input.trim() || isPending}
          >
            <LiaPaperPlane />
          </Button>
        </div>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-500">
          <LiaInfoCircleSolid className="size-4 shrink-0" />
          Artia Intelligence can make mistakes. Verify important information.
        </p>
      </form>
    </Drawer>
  );
};
